import * as Pixi from "pixi.js";
import { AdvancedBloomFilter } from "pixi-filters";

import { PersistenceLevel } from "../../config/PersistenceLevels";
import BaseEntity from "../../core/entity/BaseEntity";
import Entity, { GameEvents } from "../../core/entity/Entity";
import { lerp } from "../../core/util/MathUtil";
import MusicGenerator from "../music/MusicGenerator";
import { TempoClock } from "../music/TempoClock";

// Filter settings
const BLOOM_THRESHOLD = 0.4; // Brightness threshold for bloom effect
const BLOOM_BRIGHTNESS = 1.0; // Overall brightness multiplier
const BLOOM_BLUR = 4; // Blur amount for bloom
const BLOOM_QUALITY = 8; // Render quality (higher = better quality, slower)
const MIN_BLOOM_SCALE = 0.8; // Minimum bloom scale (how much bloom is applied at minimum)
const MAX_BLOOM_SCALE = 1.2; // Maximum bloom scale (how much bloom is applied at maximum)

// Attack/decay behavior
const BLOOM_DECAY_RATE = 0.8; // Per-frame decay multiplier (0.8 = 20% decay per frame)

// Music integration
const MUSIC_INTENSITY_EFFECT = 0.5; // How much music intensity affects bloom (0 = none, 1 = full effect)
const DOWNBEAT_MULTIPLIER = 1.8; // How much stronger downbeats are
const INTENSITY_CURVE = 1.1; // Power curve applied to final intensity

export default class BloomController extends BaseEntity implements Entity {
  id = "bloomController";
  tags = ["bloomController"];
  pausable = false; // Keep bloom effects during pause
  persistenceLevel = PersistenceLevel.Permanent;

  private bloomFilter: AdvancedBloomFilter;
  private minBloomScale: number = MIN_BLOOM_SCALE;
  private maxBloomScale: number = MAX_BLOOM_SCALE;
  private decayRate: number = BLOOM_DECAY_RATE; // Per frame decay multiplier

  constructor() {
    super();

    this.bloomFilter = new AdvancedBloomFilter({
      threshold: BLOOM_THRESHOLD,
      bloomScale: this.minBloomScale,
      brightness: BLOOM_BRIGHTNESS,
      blur: BLOOM_BLUR,
      quality: BLOOM_QUALITY,
    });
  }

  onAdd({ game }: GameEvents["add"]) {
    // Add bloom filter to stage
    game.renderer.addStageFilter(this.bloomFilter);

    // Set explicit filter area to cover entire viewport to prevent clipping
    this.updateFilterArea();
  }

  private updateFilterArea() {
    if (!this.game) return;

    // This prevents Pixi from auto-calculating a smaller filter area based on object bounds
    this.game.renderer.stage.filterArea = new Pixi.Rectangle(
      0,
      0,
      this.game.renderer.getWidth(),
      this.game.renderer.getHeight(),
    );
  }

  onWindowResize() {
    // Update filter area when window resizes to ensure bloom covers entire viewport
    this.updateFilterArea();
  }

  get musicGenerator(): MusicGenerator {
    const maybeMusicGenerator = this.game?.entities.getById("musicGenerator");
    if (maybeMusicGenerator instanceof MusicGenerator) {
      return maybeMusicGenerator;
    }
    throw new Error("MusicGenerator entity is not available");
  }

  get tempoClock(): TempoClock {
    const maybeTempoClock = this.game?.entities.getById("tempoClock");
    if (maybeTempoClock instanceof TempoClock) {
      return maybeTempoClock;
    }
    throw new Error("TempoClock entity is not available");
  }

  onTick() {
    // Find music generator and get tempo info every frame
    const tempoClock = this.tempoClock;

    if (tempoClock) {
      // Calculate beat intensity from beat phase - creates spiky pulsing effect with wide troughs
      const beatPhase = tempoClock.getBeatPhase();

      // Create a squared cosine wave for spiky peaks and wide troughs
      const cosineWave = Math.cos(beatPhase * Math.PI * 2); // -1 to 1, peaks at phase 0 (beat)
      const shiftedCosine = cosineWave + 1; // 0 to 2, peaks at phase 0
      const squaredCosine = shiftedCosine * shiftedCosine; // 0 to 4, spiky peaks at phase 0

      // Scale to bloom range: 0→0.2 (baseline), 4→1.0 (peak)
      const beatFactor = Math.max(
        0,
        Math.min(1, 0.2 + (squaredCosine / 4) * 0.8),
      );

      const isDownbeat = tempoClock.isDownbeat();
      const musicIntensity = this.musicGenerator.getParams().intensity;

      // Scale beat intensity by music intensity parameter
      // Mix between base beatFactor (when MUSIC_INTENSITY_EFFECT = 0) and music-modulated version (when = 1)
      const musicModulatedBeat = beatFactor * musicIntensity;
      const musicFactor = lerp(
        beatFactor,
        musicModulatedBeat,
        MUSIC_INTENSITY_EFFECT,
      );
      let unshapedIntensity = musicFactor;

      // Make downbeat stronger
      if (isDownbeat) {
        unshapedIntensity *= DOWNBEAT_MULTIPLIER;
      }

      // Apply curve to intensity
      const intensity = unshapedIntensity ** INTENSITY_CURVE;

      // Calculate target bloom scale
      const targetBloomScale = lerp(
        this.minBloomScale,
        this.maxBloomScale,
        intensity,
      );

      // Attack/decay logic: fast attack, slow decay
      if (targetBloomScale > this.bloomFilter.bloomScale) {
        // Attack: jump immediately to target
        this.bloomFilter.bloomScale = targetBloomScale;
      } else {
        // Decay: slowly approach target
        this.bloomFilter.bloomScale = lerp(
          this.bloomFilter.bloomScale,
          targetBloomScale,
          1 - this.decayRate,
        );
      }
    }
  }

  onDestroy({ game }: GameEvents["destroy"]) {
    game.renderer.removeStageFilter(this.bloomFilter);

    // Clean up filter area
    game.renderer.stage.filterArea = undefined;
  }
}
